import type { CSSProperties, ReactNode } from "react";
import type { PlanStatus } from "../../types.js";
import { PLAN_STATUS_BADGE } from "../../lib/plan-status.js";

/**
 * Barra de filtros (Design.md §5.3). Filtros reais: aba, Status (server-side),
 * busca por ID (client-side na página atual — não há param de busca no backend)
 * e ordenação por última atualização (client-side). "Site" é fixo em Amazon UK
 * (MVP); a aba "Rastreamento ausente" e o botão "+" são visuais/exemplo.
 */
export type ShipmentsTab = "todas" | "rastreamento-ausente";

export interface ShipmentsFiltersValue {
  tab: ShipmentsTab;
  status: PlanStatus | "all";
  search: string;
  sortDesc: boolean;
}

export interface ShipmentsFiltersProps {
  value: ShipmentsFiltersValue;
  onChange: (next: ShipmentsFiltersValue) => void;
}

const STATUS_OPTIONS = Object.entries(PLAN_STATUS_BADGE) as [
  PlanStatus,
  { label: string; tone: string }
][];

export function ShipmentsFilters({
  value,
  onChange
}: ShipmentsFiltersProps): ReactNode {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <div style={{ display: "flex", gap: "0.25rem" }}>
        <TabButton
          active={value.tab === "todas"}
          onClick={() => onChange({ ...value, tab: "todas" })}
        >
          Todas
        </TabButton>
        <TabButton
          active={value.tab === "rastreamento-ausente"}
          onClick={() => onChange({ ...value, tab: "rastreamento-ausente" })}
        >
          Rastreamento ausente
        </TabButton>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          flexWrap: "wrap"
        }}
      >
        <span style={chipStyle}>Site: Amazon UK</span>

        <label style={chipStyle}>
          Status:
          <select
            value={value.status}
            onChange={(e) =>
              onChange({ ...value, status: e.target.value as PlanStatus | "all" })
            }
            style={chipSelectStyle}
          >
            <option value="all">Todos</option>
            {STATUS_OPTIONS.map(([status, badge]) => (
              <option key={status} value={status}>
                {badge.label}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          style={chipStyle}
          onClick={() => onChange({ ...value, sortDesc: !value.sortDesc })}
          title="Ordena apenas os itens da página atual"
        >
          Última atualização: {value.sortDesc ? "mais recentes" : "mais antigas"}
        </button>

        <span aria-hidden style={{ ...chipStyle, opacity: 0.6 }}>
          +
        </span>

        <div style={{ flex: 1 }} />

        <input
          type="search"
          value={value.search}
          onChange={(e) => onChange({ ...value, search: e.target.value })}
          placeholder="Pesquisar por ID do envio"
          aria-label="Pesquisar por ID do envio"
          style={{ width: 240, maxWidth: "40vw" }}
        />
      </div>

      <p style={{ fontSize: 11, color: "var(--text-faint)", margin: 0 }}>
        Busca e ordenação aplicadas à página atual (até 20 itens). O filtro de
        Status é aplicado no servidor.
      </p>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "transparent",
        border: "none",
        borderBottom: `2px solid ${active ? "var(--orange)" : "transparent"}`,
        color: active ? "var(--text)" : "var(--text-muted)",
        fontWeight: active ? 700 : 600,
        fontSize: 13,
        padding: "0.4rem 0.6rem",
        cursor: "pointer",
        borderRadius: 0
      }}
    >
      {children}
    </button>
  );
}

const chipStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.35rem",
  background: "var(--panel)",
  border: "1px solid var(--border)",
  borderRadius: 20,
  padding: "0.35rem 0.7rem",
  fontSize: 12,
  color: "var(--text)",
  cursor: "default"
};

const chipSelectStyle: CSSProperties = {
  border: "none",
  background: "transparent",
  padding: 0,
  fontSize: 12,
  fontWeight: 600,
  color: "var(--orange)",
  cursor: "pointer"
};
